import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, Eye, EyeOff, List, ListOrdered, Paperclip, X } from "lucide-react";
import Button from "../components/Button";
import CopyButton from "../components/CopyButton";
import InlineSelect from "../components/InlineSelect";
import Modal from "../components/Modal";
import Tag from "../components/Tag";
import TextLink from "../components/TextLink";
import { apiRequest } from "../api";
import { NO_DATA, formatDate, formatDateTime, formatNumber, formatPercent, formatRelative } from "../format";
import useApiQuery from "../useApiQuery";
import { ACCOUNT_TYPES } from "./accountsData";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUS,
} from "./supportData";
import styles from "./SupportTicketPage.module.css";

const toOptions = (map) =>
  Object.entries(map).map(([value, entry]) => ({ value, label: typeof entry === "string" ? entry : entry.label }));

const CATEGORY_OPTIONS = toOptions(TICKET_CATEGORIES);
const PRIORITY_OPTIONS = toOptions(TICKET_PRIORITIES);
const STATUS_OPTIONS = toOptions(TICKET_STATUS);
const UNASSIGNED = "none";

const WITHDRAWAL_STATUS = {
  Pending: { label: "Pending", tone: "warning" },
  Paid: { label: "Paid", tone: "success" },
  Rejected: { label: "Rejected", tone: "danger" },
};

// Markers the formatting toolbar wraps around (or prefixes to) the selected reply text
const FORMATS = [
  { key: "bold", label: "Bold", wrap: "**", icon: <span className={styles.boldGlyph}>B</span> },
  { key: "italic", label: "Italic", wrap: "_", icon: <span className={styles.italicGlyph}>I</span> },
  { key: "underline", label: "Underline", wrap: "__", icon: <span className={styles.underlineGlyph}>U</span> },
  { key: "bullets", label: "Bulleted list", prefix: () => "- ", icon: <List size={16} strokeWidth={1.75} /> },
  {
    key: "numbers",
    label: "Numbered list",
    prefix: (index) => `${index + 1}. `,
    icon: <ListOrdered size={16} strokeWidth={1.75} />,
  },
];

// "Feb. 10, 2027 · 14:22" — the period is skipped for unabbreviated months like "May"
function formatStamp(date) {
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const monthLabel = month.length === 3 && month !== "May" ? `${month}.` : month;
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${monthLabel} ${date.getDate()}, ${date.getFullYear()} · ${time}`;
}

function formatFileSize(bytes) {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

let nextUpload = 0;

const prp = (value) => (value === null || value === undefined ? NO_DATA : `${formatNumber(value)} PRP`);
const drawdown = (dd) => (dd ? `${formatPercent(dd.pct)} / ${dd.limitPct}%` : NO_DATA);

// Ask the API for a presigned POST, then send the file straight to S3. Resolves to the
// { key, name } a reply attaches.
async function uploadFile(file) {
  const { key, url, fields } = await apiRequest("/api/admin/support/uploads", {
    method: "POST",
    body: { name: file.name, size: file.size, type: file.type },
  });
  const form = new FormData();
  Object.entries(fields).forEach(([field, value]) => form.append(field, value));
  form.append("file", file);
  let res;
  try {
    res = await fetch(url, { method: "POST", body: form });
  } catch {
    throw new Error("Upload failed. Check your connection and try again.");
  }
  if (!res.ok) {
    throw new Error("Upload failed. Please try again.");
  }
  return { key, name: file.name };
}
function DetailRow({ label, children }) {
  return (
    <div className={`${styles.row} d-flex align-items-center justify-content-between`}>
      <dt>{label}</dt>
      <dd className="d-flex align-items-center">{children}</dd>
    </div>
  );
}

// Links are short-lived presigned URLs, refreshed whenever the ticket reloads
function Attachment({ file }) {
  const content = (
    <>
      <Paperclip size={16} strokeWidth={1.75} aria-hidden="true" />
      <div>
        <p className={styles.attachmentName}>{file.name}</p>
        <p className={styles.attachmentSize}>{formatFileSize(file.size)}</p>
      </div>
    </>
  );
  return file.url ? (
    <a className={`${styles.attachment} d-flex`} href={file.url} target="_blank" rel="noreferrer">
      {content}
    </a>
  ) : (
    <div className={`${styles.attachment} d-flex`}>{content}</div>
  );
}

function Thread({ items: entries }) {
  const threadRef = useRef(null);
  // Every message and event sits under its own timestamp, as in the design
  const items = useMemo(
    () =>
      entries.flatMap((entry) => [
        { id: `date-${entry.id}`, kind: "date", at: formatStamp(new Date(entry.at)) },
        entry,
      ]),
    [entries]
  );

  // Keep the newest message in view as the conversation grows (without scrolling the page)
  useEffect(() => {
    threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [items.length]);

  return (
    <div ref={threadRef} className={styles.thread} aria-label="Conversation">
      {items.map((item) => {
        if (item.kind === "date") {
          return (
            <p key={item.id} className={styles.date}>
              {item.at}
            </p>
          );
        }
        if (item.kind === "event") {
          return (
            <p key={item.id} className={styles.event}>
              {item.body}
            </p>
          );
        }
        return (
          <article
            key={item.id}
            className={`${styles.message} ${item.role === "Team" ? styles.messageTeam : ""}`}
          >
            <header>
              <p className={styles.author}>{item.author}</p>
              <p className={styles.role}>{item.role}</p>
            </header>
            {item.body ? <p className={styles.body}>{item.body}</p> : null}
            {item.attachments.map((file, index) => (
              <Attachment key={`${file.name}-${index}`} file={file} />
            ))}
          </article>
        );
      })}
    </div>
  );
}

// onSend({ body, attachments, resolve }) resolves to { error } when the API refuses; the
// draft is kept until the reply lands
function Composer({ onSend }) {
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState([]); // { id, name, status: uploading | ready | error, key, error }
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const applyFormat = (format) => {
    const textarea = textareaRef.current;
    const { selectionStart: start, selectionEnd: end } = textarea;
    const selected = draft.slice(start, end);
    const replacement = format.wrap
      ? `${format.wrap}${selected}${format.wrap}`
      : (selected || "")
          .split("\n")
          .map((line, index) => `${format.prefix(index)}${line}`)
          .join("\n");

    setDraft(`${draft.slice(0, start)}${replacement}${draft.slice(end)}`);
    // Restore focus and put the caret after the inserted text
    requestAnimationFrame(() => {
      textarea.focus();
      const caret = format.wrap && !selected ? start + format.wrap.length : start + replacement.length;
      textarea.setSelectionRange(caret, caret);
    });
  };

  const updateFile = (id, changes) =>
    setFiles((current) => current.map((file) => (file.id === id ? { ...file, ...changes } : file)));

  // Each picked file uploads straight away, so sending only waits on the reply itself
  const addFiles = (event) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = "";
    picked.forEach((file) => {
      const id = (nextUpload += 1);
      if (file.size > ATTACHMENT_MAX_BYTES) {
        setFiles((current) => [
          ...current,
          { id, name: file.name, status: "error", error: `Over ${ATTACHMENT_MAX_BYTES / 1024 / 1024} MB` },
        ]);
        return;
      }
      setFiles((current) => [...current, { id, name: file.name, status: "uploading" }]);
      uploadFile(file)
        .then(({ key }) => updateFile(id, { status: "ready", key }))
        .catch((err) => updateFile(id, { status: "error", error: err.message }));
    });
  };

  const uploading = files.some((file) => file.status === "uploading");
  const ready = files.filter((file) => file.status === "ready");
  const canSend = !sending && !uploading && (draft.trim() !== "" || ready.length > 0);

  const send = async (resolve) => {
    if (!canSend) {
      return;
    }
    setSending(true);
    setError(null);
    const result = await onSend({
      body: draft.trim(),
      attachments: ready.map((file) => ({ key: file.key, name: file.name })),
      resolve,
    });
    setSending(false);
    if (result?.error) {
      setError(result.error);
      return;
    }
    setDraft("");
    setFiles([]);
  };

  return (
    <form
      className={styles.composer}
      onSubmit={(event) => {
        event.preventDefault();
        send(false);
      }}
    >
      <div className={`${styles.toolbar} d-flex align-items-center`} role="toolbar" aria-label="Formatting">
        {FORMATS.map((format) => (
          <button
            key={format.key}
            type="button"
            className={styles.formatButton}
            onClick={() => applyFormat(format)}
            aria-label={format.label}
            title={format.label}
          >
            {format.icon}
          </button>
        ))}
      </div>

      <textarea
        ref={textareaRef}
        className={styles.input}
        placeholder="Write a reply..."
        aria-label="Reply"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            send(false);
          }
        }}
      />

      {files.length ? (
        <ul className={`${styles.pendingFiles} d-flex flex-wrap`}>
          {files.map((file) => (
            <li key={file.id} className="d-flex align-items-center" title={file.error}>
              <Paperclip size={14} strokeWidth={1.75} aria-hidden="true" />
              {file.name}
              {file.status === "uploading" ? " · uploading…" : file.status === "error" ? ` · ${file.error}` : ""}
              <button
                type="button"
                className={styles.removeFile}
                onClick={() => setFiles((current) => current.filter((item) => item.id !== file.id))}
                aria-label={`Remove ${file.name}`}
              >
                <X size={14} strokeWidth={1.75} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {error ? <p className={styles.error}>{error}</p> : null}

      <div className="d-flex align-items-center justify-content-between">
        <button type="button" className={`${styles.attach} d-flex align-items-center`} onClick={() => fileInputRef.current.click()}>
          <Paperclip size={16} strokeWidth={1.75} aria-hidden="true" />
          Attach file
        </button>
        <input ref={fileInputRef} type="file" multiple hidden accept={ATTACHMENT_ACCEPT} onChange={addFiles} />

        <div className={`${styles.ctas} d-flex`}>
          <Button type="button" variant="ghost" size="sm" disabled={!canSend} onClick={() => send(true)}>
            Send &amp; resolve
          </Button>
          <Button type="submit" size="sm" disabled={!canSend}>
            {sending ? "Sending…" : "Send reply"}
          </Button>
        </div>
      </div>
    </form>
  );
}

// The trader's current trading account (GET /api/admin/accounts/:id), with their payment
// and withdrawal history behind the two buttons
function TraderCard({ trader, accountId, withdrawals }) {
  const { data, status, reload } = useApiQuery(`/api/admin/accounts/${accountId}`);
  const [showPassword, setShowPassword] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [withdrawalsOpen, setWithdrawalsOpen] = useState(false);

  const account = data?.account;
  const detail = data?.detail;
  const latestWithdrawal = withdrawals[0];

  const traderRows = account
    ? [
        [
          ["Account ID", account.login ?? NO_DATA],
          ["Account type", ACCOUNT_TYPES[account.type] ?? NO_DATA],
        ],
        [
          ["Server", detail.server ?? NO_DATA],
          [
            "Master password",
            detail.masterPassword ? (
              <>
                <span>{showPassword ? detail.masterPassword : "•••••••••"}</span>
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                </button>
                <CopyButton value={detail.masterPassword} label="Copy master password" />
              </>
            ) : (
              NO_DATA
            ),
          ],
        ],
        [
          ["Balance", prp(account.balance)],
          ["Equity", prp(account.equity)],
        ],
        [
          ["Daily DD", drawdown(account.dailyDd)],
          ["Max DD", drawdown(account.maxDd)],
        ],
        [
          ["Withdrawal eligibility", account.withdrawal ?? NO_DATA],
          ["Last withdrawal", latestWithdrawal ? formatDate(latestWithdrawal.at) : NO_DATA],
        ],
        [["Withdrawal status", latestWithdrawal ? WITHDRAWAL_STATUS[latestWithdrawal.status].label : NO_DATA]],
      ]
    : [];

  return (
    <section className={styles.card}>
      <h2 className={`${styles.cardTitle} label-font`}>Trader details</h2>
      <div className={styles.traderName}>
        <p className={styles.name}>{trader?.name ?? NO_DATA}</p>
        {trader?.email ? (
          <a className={styles.email} href={`mailto:${trader.email}`}>
            {trader.email}
          </a>
        ) : null}
      </div>
      {account ? (
        <dl className={`${styles.list} ${styles.traderGrid}`}>
          {traderRows.map((pair) => (
            <Fragment key={pair[0][0]}>
              {pair.map(([label, value]) => (
                <DetailRow key={label} label={label}>
                  {value}
                </DetailRow>
              ))}
            </Fragment>
          ))}
        </dl>
      ) : status === "error" ? (
        <p className={styles.muted}>
          Couldn't load the trading account. <TextLink arrow={false} onClick={reload}>Try again</TextLink>
        </p>
      ) : (
        <p className={styles.muted}>Loading account…</p>
      )}
      <div className={`${styles.cardCtas} d-flex flex-wrap`}>
        <Button variant="outline" size="sm" disabled={!detail} onClick={() => setPaymentsOpen(true)}>
          View payments
        </Button>
        <Button variant="outline" size="sm" onClick={() => setWithdrawalsOpen(true)}>
          View withdrawals
        </Button>
      </div>

      <Modal open={paymentsOpen} title="Payment history" onClose={() => setPaymentsOpen(false)}>
        {detail?.payments.length ? (
          <ul className={styles.withdrawalList}>
            {detail.payments.map((payment, index) => (
              <li key={`${payment.label}-${index}`} className={styles.withdrawalRow}>
                <span className={styles.withdrawalAmount}>{payment.label}</span>
                <span className={styles.withdrawalDate}>{formatDate(payment.at)}</span>
                <Tag tone={WITHDRAWAL_STATUS[payment.status]?.tone ?? "success"}>{payment.status}</Tag>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.muted}>No payments yet.</p>
        )}
      </Modal>

      <WithdrawalsModal open={withdrawalsOpen} withdrawals={withdrawals} onClose={() => setWithdrawalsOpen(false)} />
    </section>
  );
}

function WithdrawalsModal({ open, withdrawals, onClose }) {
  return (
    <Modal open={open} title="Withdrawal history" onClose={onClose}>
      {withdrawals.length ? (
        <ul className={styles.withdrawalList}>
          {withdrawals.map((item) => (
            <li key={item.id} className={styles.withdrawalRow} title={`${item.label} · ${item.reference}`}>
              <span className={styles.withdrawalAmount}>{formatNumber(item.amount, 2)} USDC</span>
              <span className={styles.withdrawalDate}>{formatDate(item.at)}</span>
              <Tag tone={WITHDRAWAL_STATUS[item.status].tone}>{WITHDRAWAL_STATUS[item.status].label}</Tag>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.muted}>No withdrawals yet.</p>
      )}
    </Modal>
  );
}

export default function SupportTicketPage({ ticketId, onNavigate }) {
  const path = `/api/admin/support/tickets/${ticketId}`;
  const { data, setData, status: loadStatus, reload } = useApiQuery(path);
  const team = useApiQuery("/api/admin/team");
  const [noteDraft, setNoteDraft] = useState("");
  const [actionError, setActionError] = useState(null);
  const [withdrawalsOpen, setWithdrawalsOpen] = useState(false);

  const ticket = data?.ticket;

  const assigneeOptions = useMemo(() => {
    const members = (team.data?.members ?? [])
      .filter((member) => member.status === "active")
      .map((member) => ({ value: member.id, label: member.name }));
    // Someone removed from the team still shows as the current assignee
    if (ticket?.assignee && !members.some((member) => member.value === ticket.assignee.id)) {
      members.push({ value: ticket.assignee.id, label: ticket.assignee.name });
    }
    return [{ value: UNASSIGNED, label: "Unassigned" }, ...members];
  }, [team.data, ticket?.assignee]);

  if (!ticket) {
    const notFound = loadStatus === "error" && !data;
    return (
      <section className={styles.missing}>
        <h1 className={styles.title}>{loadStatus === "loading" ? `Ticket ${ticketId}` : "Couldn't load this ticket"}</h1>
        <p>
          {loadStatus === "loading" ? (
            "Loading…"
          ) : notFound ? (
            <>
              Ticket #{ticketId} doesn't exist or couldn't be loaded.{" "}
              <button type="button" className={styles.backLink} onClick={reload}>
                Try again
              </button>{" "}
              or{" "}
              <button type="button" className={styles.backLink} onClick={() => onNavigate("/support")}>
                go back to support
              </button>
            </>
          ) : null}
        </p>
      </section>
    );
  }

  const statusInfo = TICKET_STATUS[ticket.status];

  // Every write returns the whole ticket, event lines included
  const write = async (suffix, method, body) => {
    setActionError(null);
    try {
      const result = await apiRequest(`${path}${suffix}`, { method, body });
      setData(result);
      return result;
    } catch (err) {
      setActionError(err.message);
      return { error: err.message };
    }
  };

  const change = (field) => (next) => {
    const value = field === "assignee" && next === UNASSIGNED ? null : next;
    const current = field === "assignee" ? ticket.assignee?.id ?? null : ticket[field];
    if (value !== current) {
      write("", "PATCH", { [field]: value });
    }
  };

  const addNote = async (event) => {
    event.preventDefault();
    const body = noteDraft.trim();
    if (!body) {
      return;
    }
    const result = await write("/notes", "POST", { body });
    if (!result.error) {
      setNoteDraft("");
    }
  };

  return (
    <section className={styles.page}>
      <header className={`${styles.header} d-flex align-items-start justify-content-between`}>
        <div className={styles.heading}>
          <nav className={`${styles.breadcrumbs} label-font d-flex align-items-center`} aria-label="Breadcrumb">
            <button type="button" onClick={() => onNavigate("/support")}>
              Support
            </button>
            <ChevronRight size={16} strokeWidth={1.75} aria-hidden="true" />
            <span aria-current="page">#{ticket.id}</span>
          </nav>
          <div className={`${styles.titleRow} d-flex align-items-center`}>
            <h1 className={styles.title}>Ticket {ticket.id}</h1>
            <Tag tone={statusInfo.tone}>{statusInfo.label}</Tag>
          </div>
          <p className={styles.subject}>{ticket.subject}</p>
        </div>
        <div className={`${styles.headerCtas} d-flex`}>
          <Button variant="outline" disabled={ticket.status === "resolved"} onClick={() => change("status")("resolved")}>
            Resolve
          </Button>
          <button
            type="button"
            className={styles.closeCta}
            onClick={() => onNavigate("/support")}
            aria-label="Back to support"
            title="Back to support"
          >
            <X size={24} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
      </header>

      {actionError ? <p className={styles.error}>{actionError}</p> : null}

      <div className={`${styles.layout} d-flex align-items-start`}>
        <div className={`${styles.conversation} d-flex flex-column`}>
          <Thread items={ticket.thread} />
          <Composer onSend={(reply) => write("/messages", "POST", reply)} />
        </div>

        <div className={`${styles.sidebar} d-flex flex-column`}>
          <section className={styles.card}>
            <h2 className={`${styles.cardTitle} label-font`}>Ticket details</h2>
            <dl className={styles.list}>
              <DetailRow label="Category">
                <InlineSelect
                  label="Category"
                  options={CATEGORY_OPTIONS}
                  value={ticket.category}
                  onChange={change("category")}
                />
              </DetailRow>
              <DetailRow label="Priority">
                <InlineSelect
                  label="Priority"
                  options={PRIORITY_OPTIONS}
                  value={ticket.priority}
                  onChange={change("priority")}
                />
              </DetailRow>
              <DetailRow label="Created">{formatDateTime(ticket.createdAt)}</DetailRow>
              <DetailRow label="Last updated">{formatRelative(ticket.lastActivityAt)}</DetailRow>
              <DetailRow label="Assigned employee">
                <InlineSelect
                  label="Assigned employee"
                  options={assigneeOptions}
                  value={ticket.assignee?.id ?? UNASSIGNED}
                  onChange={change("assignee")}
                />
              </DetailRow>
              <DetailRow label="Status">
                <InlineSelect
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={ticket.status}
                  onChange={change("status")}
                  renderValue={(option) => <Tag tone={TICKET_STATUS[option.value].tone}>{option.label}</Tag>}
                />
              </DetailRow>
            </dl>
          </section>

          {ticket.accountId ? (
            <TraderCard trader={ticket.trader} accountId={ticket.accountId} withdrawals={ticket.withdrawals} />
          ) : (
            <section className={styles.card}>
              <h2 className={`${styles.cardTitle} label-font`}>Trader details</h2>
              <div className={styles.traderName}>
                <p className={styles.name}>{ticket.trader?.name ?? NO_DATA}</p>
                {ticket.trader?.email ? (
                  <a className={styles.email} href={`mailto:${ticket.trader.email}`}>
                    {ticket.trader.email}
                  </a>
                ) : null}
              </div>
              <p className={styles.muted}>No trading account yet.</p>
              <div className={`${styles.cardCtas} d-flex flex-wrap`}>
                <Button variant="outline" size="sm" onClick={() => setWithdrawalsOpen(true)}>
                  View withdrawals
                </Button>
              </div>
              <WithdrawalsModal
                open={withdrawalsOpen}
                withdrawals={ticket.withdrawals}
                onClose={() => setWithdrawalsOpen(false)}
              />
            </section>
          )}

          <section className={styles.card}>
            <h2 className={`${styles.cardTitle} label-font`}>Internal notes</h2>
            {ticket.notes.map((note) => (
              <article key={note.id} className={styles.note}>
                <p className={styles.noteBody}>{note.body}</p>
                <p className={styles.noteMeta}>
                  By {note.author ?? "an admin"} on {formatDateTime(note.at)}
                </p>
              </article>
            ))}
            <form className={`${styles.noteForm} d-flex`} onSubmit={addNote}>
              <input
                className={styles.noteInput}
                placeholder="Add note..."
                aria-label="Add note"
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
              />
              <Button type="submit" variant="outline" size="sm" disabled={!noteDraft.trim()}>
                Add
              </Button>
            </form>
          </section>
        </div>
      </div>
    </section>
  );
}
